import { ComponentPropsWithoutRef, forwardRef, MouseEvent } from "react";
import { Icon } from "../../Icon";
import { Flex, FlexProps } from "../../Flex";
import Check from "@servicetitan/hammer-icon/mdi/round/check.svg";
import { DataTrackingId } from "../../../types";

import cx from "classnames";
import styles from "./PickerList.module.scss";

/**
 * Props for the PickerListOption component
 * @extends ComponentPropsWithoutRef<"li">
 */
export type PickerListOptionProps = ComponentPropsWithoutRef<"li"> & {
  /** The item object containing label, value, and optional disabled state */
  item: { label: string; value: number; disabled?: boolean };
  /** Whether this option is currently selected */
  isSelected: boolean;
  /** Callback when this option is selected */
  onSelectionChange: (
    selected: { label: string; value: number } | undefined,
  ) => void;
  /** Flex alignment for items */
  alignItems?: FlexProps["alignItems"];
  /** Flex justification for items */
  justifyItems?: FlexProps["justifyItems"];
  /** Flex alignment for content */
  alignContent?: FlexProps["alignContent"];
  /** Flex justification for content */
  justifyContent?: FlexProps["justifyContent"];
  /** Flex place-items property */
  placeItems?: FlexProps["placeItems"];
  /** Flex place-content property */
  placeContent?: FlexProps["placeContent"];
} & DataTrackingId;

export const PickerListOption = forwardRef<
  HTMLLIElement,
  PickerListOptionProps
>((props, ref) => {
  const {
    className,
    children,
    onClick,
    item,
    isSelected,
    onSelectionChange,
    alignItems,
    justifyItems,
    alignContent,
    justifyContent,
    placeItems,
    placeContent,
    ...rest
  } = props;

  const disabled = item.disabled || false;

  const handleSelection = () => {
    if (disabled) return;

    if (isSelected) {
      onSelectionChange(undefined);
    } else {
      onSelectionChange(item);
    }
  };

  const onClickHandler = (e: MouseEvent<HTMLLIElement>) => {
    onClick?.(e);
    handleSelection();
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelection();
    }
  };

  const PickerListOptionClassNames = cx(
    styles["picker-list-option"],
    className,
  );

  return (
    <li
      className={PickerListOptionClassNames}
      data-anv="picker-list-option"
      role="option"
      aria-disabled={disabled}
      aria-selected={isSelected}
      data-item-label={item.label}
      onClick={onClickHandler}
      onKeyDown={onKeyDownHandler}
      tabIndex={-1}
      ref={ref}
      {...rest}
    >
      <Flex
        className={styles["children"]}
        grow={1}
        shrink={1}
        alignItems={alignItems}
        justifyItems={justifyItems}
        alignContent={alignContent}
        justifyContent={justifyContent}
        placeItems={placeItems}
        placeContent={placeContent}
      >
        {children || item.label}
      </Flex>
      <Icon
        aria-hidden
        svg={Check}
        inherit
        className={styles["Icon"]}
        style={{ opacity: isSelected ? 1 : 0 }}
      />
    </li>
  );
});

PickerListOption.displayName = "PickerListOption";
